import { Loader2, RefreshCw } from 'lucide-react';
import * as QRCode from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { RefreshQrCodeIcon } from './refresh-qr-code-icon';
import type { UseMutationResult } from '@tanstack/react-query';

const INLINE_CODE_CLASSNAME =
  'relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-xs';

export interface WebsiteBannerPreviewSidebarProps {
  tenantId: string;
  bannerId: string | null;
  liveSessionToken: string | null;
  livePreviewUrl: string;
  liveSessionMutation: UseMutationResult<
    string,
    Error,
    { path: { bannerId: string } }
  >;
  forceRefreshMutation: UseMutationResult<void, Error, void>;
  LL: {
    websiteBannerPage: {
      preview: {
        loadTenantFirst: () => string;
      };
      livePreview: {
        saveBannerFirst: () => string;
        startingSession: () => string;
        scanWithPhone: () => string;
        newQrCode: () => string;
        forceRefresh: () => string;
        couldNotStartSession: () => string;
      };
    };
  };
}

export function WebsiteBannerPreviewSidebar({
  tenantId,
  bannerId,
  liveSessionToken,
  livePreviewUrl,
  liveSessionMutation,
  forceRefreshMutation,
  LL,
}: WebsiteBannerPreviewSidebarProps) {
  return (
    <div className="border-l p-6 bg-muted/10 h-full">
      <div className="sticky top-6">
        {!tenantId ? (
          <p className="text-sm text-muted-foreground">
            {LL.websiteBannerPage.preview.loadTenantFirst()}
          </p>
        ) : !bannerId ? (
          <p className="text-sm text-muted-foreground">
            {LL.websiteBannerPage.livePreview.saveBannerFirst()}
          </p>
        ) : liveSessionMutation.isPending && !liveSessionToken ? (
          <div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span className="text-sm">
              {LL.websiteBannerPage.livePreview.startingSession()}
            </span>
          </div>
        ) : liveSessionToken ? (
          <div className="flex flex-col items-center gap-3">
            <p className="text-sm text-muted-foreground text-center">
              {LL.websiteBannerPage.livePreview.scanWithPhone()}
            </p>
            <div className="border border-border rounded-lg p-3 bg-background">
              <QRCode.QRCodeSVG
                value={livePreviewUrl}
                size={200}
                bgColor="#ffffff"
                fgColor="#000000"
                level="H"
                includeMargin={true}
              />
            </div>
            <code
              className={`${INLINE_CODE_CLASSNAME} block max-w-full break-all text-center text-xs`}
            >
              {livePreviewUrl}
            </code>
            <div className="flex flex-col gap-1.5 w-full max-w-[200px]">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  liveSessionMutation.mutate({
                    path: { bannerId: bannerId! },
                  })
                }
                disabled={liveSessionMutation.isPending && !liveSessionToken}
              >
                {liveSessionMutation.isPending && !liveSessionToken ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshQrCodeIcon className="h-4 w-4 text-foreground" />
                )}
                <span className="ml-1.5">
                  {LL.websiteBannerPage.livePreview.newQrCode()}
                </span>
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => forceRefreshMutation.mutate()}
                disabled={!liveSessionToken || forceRefreshMutation.isPending}
              >
                {forceRefreshMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                <span className="ml-1.5">
                  {LL.websiteBannerPage.livePreview.forceRefresh()}
                </span>
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            {LL.websiteBannerPage.livePreview.couldNotStartSession()}
          </p>
        )}
      </div>
    </div>
  );
}
